import type { CurrentFreshness, DatasetFreshness, FreshnessStatus, SourceFreshnessStatus } from '../types/freshness'
import { tForLocale, type Locale } from '../i18n/locale'

export const HOUR_MS = 60 * 60 * 1000

function timestamp(value?: string | null) {
  if (!value) return undefined
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function freshnessStatus(referenceAt: string | null | undefined, now = new Date()): FreshnessStatus {
  const reference = timestamp(referenceAt)
  if (reference === undefined) return 'no-data'
  const ageHours = Math.max(0, (now.getTime() - reference) / HOUR_MS)
  if (ageHours <= 24) return 'normal'
  if (ageHours <= 48) return 'delayed'
  return 'expired'
}

export function freshnessAgeHours(referenceAt: string | null | undefined, now = new Date()) {
  const reference = timestamp(referenceAt)
  if (reference === undefined) return null
  return Math.max(0, (now.getTime() - reference) / HOUR_MS)
}

export function sourceFreshnessStatus(
  source: DatasetFreshness['sources']['Polymarket'],
  now = new Date(),
): SourceFreshnessStatus {
  if (source.status === 'degraded' || source.lastAttemptStatus === 'failed') return 'degraded'
  if (!source.lastSuccessfulSyncAt) return 'no-data'
  const status = freshnessStatus(source.lastSuccessfulSyncAt, now)
  if (status === 'normal') return 'success'
  return status
}

function combineFreshnessStatuses(baseStatus: FreshnessStatus, sourceStatuses: SourceFreshnessStatus[]) {
  if (sourceStatuses.includes('degraded')) return 'degraded' as const
  if (sourceStatuses.includes('expired') || baseStatus === 'expired') return 'expired' as const
  if (sourceStatuses.includes('delayed') || baseStatus === 'delayed') return 'delayed' as const
  if (baseStatus === 'no-data' && sourceStatuses.every((status) => status === 'no-data')) return 'no-data' as const
  return baseStatus
}

export function withCurrentFreshness(payload: DatasetFreshness, now = new Date()): CurrentFreshness {
  const generatedReference = payload.freshness?.referenceAt
    ?? payload.dataset.presentationGeneratedAt
    ?? payload.dataset.eventGeneratedAt
    ?? payload.dataset.catalogGeneratedAt
  const baseStatus = freshnessStatus(generatedReference, now)
  const sourceStatuses = Object.values(payload.sources ?? {}).map((source) => sourceFreshnessStatus(source, now))
  const status = payload.sync?.status === 'failed'
    ? 'degraded'
    : combineFreshnessStatuses(baseStatus, sourceStatuses)
  return {
    ...payload,
    current: {
      status,
      ageHours: freshnessAgeHours(generatedReference, now),
      referenceAt: generatedReference ?? null,
    },
  }
}

export function freshnessStatusLabel(status: FreshnessStatus, locale: Locale = 'zh-CN') {
  if (status === 'normal') return tForLocale(locale, 'common.normalSync')
  if (status === 'delayed') return tForLocale(locale, 'common.syncDelayedLong')
  if (status === 'expired') return tForLocale(locale, 'common.expiredLong')
  if (status === 'degraded') return tForLocale(locale, 'common.degradedLong')
  return tForLocale(locale, 'common.noDataLong')
}

export function sourceFreshnessStatusLabel(status: SourceFreshnessStatus, locale: Locale = 'zh-CN') {
  if (status === 'success') return tForLocale(locale, 'common.normalSync')
  if (status === 'delayed') return tForLocale(locale, 'common.delayed')
  if (status === 'expired') return tForLocale(locale, 'common.expired')
  if (status === 'degraded') return tForLocale(locale, 'common.degraded')
  if (status === 'disabled') return tForLocale(locale, 'common.hidden')
  if (status === 'no-data') return tForLocale(locale, 'common.noData')
  return tForLocale(locale, 'common.unknown')
}

export function isDeadlinePassed(deadline?: string, now = new Date()) {
  const parsed = timestamp(deadline)
  return parsed !== undefined && parsed < now.getTime()
}
